/**
 * Quando il lettore di badge e il cartellino raccontano due giornate diverse.
 *
 * Quarto blocco delle timbrature ZKTeco (registro, K94). Di qua ci sono i **nomi** dei modi in
 * cui i due lati possono non coincidere, perché quei nomi li scrivono tre posti — il server che
 * li calcola, il pannello che li mostra all'ufficio del personale, l'app che li mostra alla
 * persona in tre lingue — e riscritti a mano diventerebbero tre elenchi che si allontanano.
 *
 * ## Perché sono sette e non uno
 *
 * «Divergente sì/no» è la risposta che non serve a nessuno. Chi apre la coda deve sapere *in che
 * modo* diverge, perché le sette righe qui sotto si chiudono in sette modi diversi: un lettore
 * fermo e un badge dimenticato non si sistemano con lo stesso gesto e non hanno lo stesso
 * seguito. E la stessa giornata può divergere in più modi insieme — il lettore l'ha vista
 * arrivare prima **e** il cartellino dice che è rimasta dopo — per cui viaggiano in un elenco e
 * non in una parola sola.
 *
 * ## La direzione, che qui non c'è e conta lo stesso
 *
 * Nessuno di questi sette nomi dice «entrata» o «uscita». Il lettore il verso della passata non
 * lo scrive — è la ragione per cui `badge_days` non ha una colonna per il verso, vedi K93 — e
 * per fortuna il confronto non ne ha bisogno: **una passata alle 07:12 dimostra che quella
 * persona era qui alle 07:12**, in qualunque direzione stesse camminando. Tutto quello che si
 * confronta sono istanti e minuti fra istanti, che si sanno.
 */
import { WorkDayVerdict } from './enums.js';
/**
 * I sette modi in cui una giornata può risultare contesa.
 *
 * I quattro `*_PRIMA` / `*_DOPO` valgono solo oltre la tolleranza in minuti che sta nelle
 * impostazioni: chi timbra dallo spogliatoio passa dal lettore qualche minuto prima, e chiamare
 * divergenza quei quattro minuti riempirebbe la coda di righe vere e inutili — che è il modo
 * più rapido di far smettere qualcuno di guardarla.
 */
export const DIVERGENZE = [
    /** Il lettore l'ha vista, il cartellino non esiste: nessuna sessione quel giorno. */
    'SOLO_LETTORE',
    /** C'è la sessione, il lettore non ha visto passare nessuno. */
    'SOLO_CARTELLINO',
    /** La prima passata è prima dell'inizio dichiarato, oltre la tolleranza. */
    'LETTORE_PRIMA',
    /** L'ultima passata è dopo la fine dichiarata, oltre la tolleranza. */
    'LETTORE_DOPO',
    /** La sessione comincia prima della prima passata, oltre la tolleranza. */
    'CARTELLINO_PRIMA',
    /** La sessione finisce dopo l'ultima passata, oltre la tolleranza. */
    'CARTELLINO_DOPO',
    /**
     * Una rettifica c'è, ma i numeri si sono mossi dopo la firma.
     *
     * Non è una divergenza fra i due lati: è una divergenza fra quello che una persona aveva
     * davanti quando ha deciso e quello che c'è scritto adesso. Sta in questo elenco perché il
     * seguito è lo stesso — la giornata torna nella coda — e perché senza di lei una rettifica
     * coprirebbe in silenzio dei dati che non ha mai visto.
     */
    'RETTIFICA_SCADUTA',
];
/**
 * Come si chiamano in italiano, per il pannello.
 *
 * L'app le dice in tre lingue e le prende dai suoi dizionari: qui c'è la sola italiana, che è la
 * lingua in cui è scritto il pannello. Il `Record` non compila finché un nome nuovo non ha la
 * sua frase, che è il momento in cui si sa come chiamarlo.
 */
export const DIVERGENZA_LABELS = {
    SOLO_LETTORE: 'Passata di badge senza cartellino',
    SOLO_CARTELLINO: 'Cartellino senza passate di badge',
    LETTORE_PRIMA: 'Vista dal lettore prima dell’inizio dichiarato',
    LETTORE_DOPO: 'Vista dal lettore dopo la fine dichiarata',
    CARTELLINO_PRIMA: 'Timbratura iniziata prima della prima passata',
    CARTELLINO_DOPO: 'Timbratura chiusa dopo l’ultima passata',
    RETTIFICA_SCADUTA: 'Rettifica presa su numeri che nel frattempo sono cambiati',
};
/**
 * Le tre divergenze in cui **il lettore dice di più**, ed è la metà che protegge la persona.
 *
 * La decisione del 2026-08-05 — «vince il lettore, la divergenza si segnala» — vale qui dentro e
 * non fuori, e la ragione è nella riga stessa della decisione: il caso normale non è la
 * furbizia, è il lettore fermo, il badge dimenticato, l'ingresso dal cancello merci. Quando il
 * lettore dice **di più** — l'ha vista prima, l'ha vista dopo, l'ha vista in un giorno senza
 * cartellino — quello che dice è un fatto in più e il sistema lo propone. Quando dice **di
 * meno** — non ha visto niente, o meno di quanto dichiarato — non vince nessuno: scegliere in
 * silenzio toglierebbe delle ore a una persona sulla base di una macchina che può essere stata
 * ferma, e nessuna riga di codice ha il titolo per farlo.
 *
 * «Vince» non vuol dire nemmeno «decide da solo» nella direzione buona. Anche lì il sistema
 * propone e la firma la mette un cristiano, perché i quaranta minuti fra la prima passata e
 * l'inizio della timbratura possono essere la colazione in mensa: aggiungerli in automatico
 * sarebbe una bugia a favore della persona, che resta una bugia e in una contestazione non la
 * difende nessuno.
 */
export const DIVERGENZE_A_FAVORE = [
    'SOLO_LETTORE',
    'LETTORE_PRIMA',
    'LETTORE_DOPO',
];
/**
 * Le altre, quelle in cui il lettore dice di meno o non dice niente di nuovo.
 *
 * Elencate a mano e poi controllate dal compilatore: la sottrazione qui sotto non lascia niente
 * solo se le due liste insieme contengono tutti e sette i nomi. Una divergenza nuova che
 * qualcuno aggiunga senza decidere da che parte sta non compila, ed è precisamente la domanda
 * che non deve poter restare senza risposta.
 */
export const DIVERGENZE_DA_GUARDARE = [
    'SOLO_CARTELLINO',
    'CARTELLINO_PRIMA',
    'CARTELLINO_DOPO',
    'RETTIFICA_SCADUTA',
];
/**
 * I tre verdetti, nell'ordine in cui si presentano a chi firma.
 *
 * L'ordine non è alfabetico ed è l'unica cosa che questo elenco aggiunge al tipo: chi apre la
 * rettifica trova per primo il caso che il sistema ha già proposto nella maggioranza delle
 * giornate contese — il lettore ha visto qualcosa che il cartellino non racconta — e per ultimo
 * quello che va scritto a mano. Un `Record` non ha un ordine di cui ci si possa fidare per
 * disegnare tre righe sullo schermo, e tre righe che cambiano posto sono tre righe che si
 * scelgono male.
 */
export const VERDETTI = [
    WorkDayVerdict.READER,
    WorkDayVerdict.TIMESHEET,
    WorkDayVerdict.NEITHER,
];
/**
 * Come si chiamano in italiano i tre verdetti, per il pannello.
 *
 * Sono frasi e non parole di proposito. «Lettore» da solo, accanto a una casella da spuntare, non
 * dice se si sta scegliendo *chi ha ragione* o *chi ha sbagliato*: sono due letture opposte della
 * stessa parola, e chi firma le tiene distinte solo finché è concentrato. La frase intera dice il
 * gesto: quali minuti finiscono in busta paga.
 */
export const VERDETTO_LABELS = {
    [WorkDayVerdict.READER]: 'Valgono le passate di badge',
    [WorkDayVerdict.TIMESHEET]: 'Vale il cartellino',
    [WorkDayVerdict.NEITHER]: 'Non vale nessuno dei due',
};
/**
 * Che cosa vuol dire scegliere ciascuno dei tre, in una riga sotto il nome.
 *
 * Serve al terzo, che è quello che sembra una svista e non lo è: il lettore l'ha vista passare e
 * il cartellino tace perché quel giorno era in ferie. Nessuna delle due storie è la giornata di
 * lavoro, e i minuti li scrive chi firma. Gli altri due stanno qui per non lasciare il terzo da
 * solo con una spiegazione — una riga spiegata in mezzo a due righe mute si legge come l'unica
 * complicata, e viene evitata.
 */
export const VERDETTO_SPIEGAZIONI = {
    [WorkDayVerdict.READER]: 'La giornata vale quanto dicono la prima e l’ultima passata al lettore.',
    [WorkDayVerdict.TIMESHEET]: 'La giornata vale quanto dicono le sessioni dichiarate.',
    [WorkDayVerdict.NEITHER]: 'Né l’uno né l’altro raccontano questa giornata: i minuti li scrivi tu qui sotto.',
};
//# sourceMappingURL=presenze.js.map